import {DateTime, Duration} from 'luxon'
import {isTruthy, last} from 'remeda'
import {notNullOrEmpty} from 'shared/collection'
import numeral from 'numeral'
const {floor, log10} = Math

const {fromISO} = DateTime
export const fromTemporalISO = (temporalISo: string) => {
  const {iso, zone} = /^(?<iso>.*?)(\[(?<zone>.*)\])?$/.exec(temporalISo)?.groups ?? {}
  if (!iso) throw new Error(`Invalid temporalISO: ${temporalISo}`)
  return DateTime.fromISO(iso, {zone})
}

const unitNames = ['day', 'hour', 'minute', 'second'] as const
type Unit = (typeof unitNames)[number]
type UnitMd = Record<(typeof unitNames)[number], {short: string}>
const unitsMd = {
  day: {short: 'day'},
  hour: {short: 'hr'},
  minute: {short: 'min'},
  second: {short: 'sec'}
} satisfies UnitMd

const {abs, round} = Math

export const fmtDuration = (
  dur: Duration,
  options?: {minUnit?: Unit; minSecondUnit?: Unit; prefix?: string; suffix?: string}
) => {
  const fmt = (n: number, unit: Unit) => {
    const {short} = unitsMd[unit]
    return n == 0 ? '' : `${floor(abs(n))} ${short}${n > 1 ? 's' : ''}`
  }
  const {minUnit, minSecondUnit, prefix, suffix} = options ?? {}
  const unitSubset = minUnit ? unitNames.slice(0, unitNames.indexOf(minUnit) + 1) : unitNames
  const isPast = dur.as('milliseconds') < 0
  const durAbs = isPast ? dur.negate() : dur

  // start from the largest units used and work down to find what units to display
  // it isn't simple because we want to be able to display 1d 5mins if there is no hrs portion
  const displayUnits: Unit[] = []
  let durUsed = durAbs
  for (const u of unitSubset) {
    const timeTotal = durUsed.as(u)
    if (timeTotal >= 1 || u == minUnit) {
      displayUnits.push(u)
      durUsed = durUsed.minus({[u]: floor(timeTotal)})
    }
    if (displayUnits.length == 2 || (displayUnits.length == 1 && u == minSecondUnit)) break
  }

  // round each unit and then normalize it
  const durInDisplay = durAbs // start with absolute because we describe in the text (i.e. ago , or in)
    .shiftTo(...displayUnits) // use the display units we worked out before
    .mapUnits(v => round(v)) // round like we display (59.5 minutes turns into 60 after rounding, then we want that to display as an extra hour)
    .normalize()

  const isNow = displayUnits.length == 0 || durInDisplay.as(last(displayUnits)!) < 0.5
  return isNow ? 'now' : (
      `${prefix ?? (isPast ? '' : 'in ')}${displayUnits
        .map(u => fmt(durInDisplay.get(u), u))
        .filter(notNullOrEmpty)
        .join(' ')}${(suffix ?? isPast) ? ' ago' : ''}`
    )
}

export type FmtDateOptions = {
  showTime?: boolean
  now?: DateTime
  minUnit?: Unit
  minSecondUnit?: Unit
  showTz?: boolean
  disableRelative?: boolean
  /** if true will hide years/months matching the current date, otherwise hides matching according to the given date */
  hideMatching?: DateTime | boolean
  hideMatchingMode?: 'year-month' | 'year'
}

/**
 * Formats a date nicely.
 *  If it's within the last week, it will show as a relative date (e.g. "2 days 1h ago") otherwise will show the full date
 */
export const fmtDate = (date?: string | DateTime | null, options?: FmtDateOptions) => {
  const {
    showTime,
    minUnit,
    minSecondUnit = 'minute',
    showTz = false,
    disableRelative,
    hideMatching: hideMatchingRaw,
    hideMatchingMode = 'year'
  } = options ?? {}
  if (date == null) return ''
  const now = options?.now ?? DateTime.now()
  const d = DateTime.isDateTime(date) ? date : fromISO(date)
  const ago = d.diff(now)
  const hideMatching =
    hideMatchingRaw == true ? now
    : DateTime.isDateTime(hideMatchingRaw) ? hideMatchingRaw
    : null
  if (Math.abs(ago.as('days')) <= 7 && !disableRelative) return fmtDuration(ago, {minUnit, minSecondUnit})
  const fmt = [
    'd',
    !(
      hideMatching &&
      hideMatchingMode == 'year-month' &&
      d.year == hideMatching.year &&
      d.month == hideMatching.month
    ) && ' LLL',
    !(hideMatching && d.year == hideMatching.year) && ' yyyy',
    showTime && ', H:mm'
  ]
    .filter(isTruthy)
    .join('')
  return `${d.toFormat(fmt)}${d.offset > 0 && showTz ? ` (${d.zone.name})` : ''}`
}

export const parseNum = (t?: string) => numeral(t ?? '').value()

/**
 * formats a number nicely. Uses the formatting from numeral http://numeraljs.com with some automation and ability to specify rounding function
 */
export const fmtNum = (n: number, f?: string, options?: {showSign?: boolean}) => {
  const re =
    /(?:^\"(?<prefix>[^\"]*)\")?(?<format>[^"\n|]+)(?:\|(?<round>floor|ceil|round))?(?:\"(?<suffix>[^\"]*)\")?/im
  const m = f?.match(re)?.groups as
    | {
        prefix?: string
        format: string
        suffix?: string
        round?: 'floor' | 'ceil' | 'round'
      }
    | undefined
  const fmt = m?.format?.trim() == 'a' ? autoFormat(n) : (m?.format ?? autoFormat(n))
  const s = `${m?.prefix ?? ''}${
    options?.showSign ?
      n > 0 ?
        '+'
      : ''
    : ''
  }${numeral(n).format(fmt, m?.round ? Math[m.round] : undefined)}${m?.suffix ?? ''}`
  if (s == 'NaN') console.warn('fmtNum NaN', {n, f, options})
  return s
}

// if we are in single digits for whatever units (K, M), then show 1d place, otherwise whole numbers
const autoFormat = (n: number) => (n < 1 || floor(log10(n)) % 3 == 0 ? '0[.]0a' : '0a')

export const fmtWholePct = (pct?: number, options?: {showSign?: boolean; decimals?: number}) =>
  pct != null ?
    `${
      (options?.showSign ?
        pct > 0 ?
          '+'
        : ''
      : '') + pct.toFixed(options?.decimals ?? 0)
    }%`
  : ''

export const indent = (s: string, options?: {indent?: string; firstLine?: boolean}) =>
  s
    .split('\n')
    .map((l, i) => `${options?.firstLine && i == 0 ? '' : (options?.indent ?? '  ')}${l}`)
    .join('\n')
